/* Progressive enhancements only. Commerce remains owned by the theme's Product/Cart islands. */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const animateScreenChange = element => reduced.matches ? null : element.animate([
    { opacity: .2, filter: 'blur(3px)', transform: 'translateX(-2px)' },
    { opacity: .8, filter: 'blur(1px)', transform: 'translateX(1px)', offset: .55 },
    { opacity: 1, filter: 'blur(0)', transform: 'translateX(0)' }
  ], { duration: 240, easing: 'ease-out' });
  const define = (name, component) => { if (!customElements.get(name)) customElements.define(name, component); };
  const listen = (el, event, fn, signal, extra = {}) => el.addEventListener(event, fn, { signal, ...extra });
  const resolveText = (value, progress) => {
    const cutoff = Math.ceil(value.length * progress);
    return [...value].map((char, i) => i < cutoff || /\s/.test(char) ? char : '░▒/0123456789'[Math.floor(Math.random() * 13)]).join('');
  };
  let liftedCard = null;
  const cardControl = target => target.closest('button,input,select,textarea,label,summary,[role="button"],a:not([data-bm-card-surface])');
  const liftCard = card => {
    if (liftedCard === card) return;
    liftedCard?.removeAttribute('data-bm-lifted');
    liftedCard = card;
    liftedCard?.setAttribute('data-bm-lifted', '');
    liftedCard?.dispatchEvent(new CustomEvent('plndr:record-highlight', { bubbles: true }));
  };
  document.addEventListener('click', event => {
    if (liftedCard && (!liftedCard.contains(event.target) || cardControl(event.target))) liftCard(null);
  }, true);
  // Hit-test stable grid slots, not the moving card face. Continue sampling
  // through a native pan and its momentum; none of these handlers cancel input.
  let activeTouch = null;
  let scrollTouch = null;
  let touchFrame = 0;
  let scrollUntil = 0;
  const stopTouchTracking = () => {
    activeTouch = null; scrollTouch = null; scrollUntil = 0;
    cancelAnimationFrame(touchFrame); touchFrame = 0;
  };
  const markLift = card => { card?.setAttribute('data-bm-touch', ''); liftCard(card); };
  const slotAt = sample => {
    const { x, y, list } = sample;
    if (!list?.isConnected || list.hasAttribute('data-bm-filtering')) return null;
    const bounds = list.getBoundingClientRect();
    if (x < bounds.left - 6 || x > bounds.right + 6 || y < bounds.top - 8 || y > bounds.bottom + 8) return null;
    // Header, menu and modal overlays must not select cards behind them.
    const hit = document.elementFromPoint(x, y);
    if (hit && !list.contains(hit) && hit !== list) return null;
    let nearby = null;
    for (const slot of list.children) {
      if (slot.classList.contains('bm-filter-hidden') || slot.inert) continue;
      const card = slot.querySelector('plndr-record.bm-card');
      if (!card) continue;
      const rect = slot.getBoundingClientRect();
      if (!rect.width || !rect.height) continue;
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return card;
      // Small gutters and the 7px visual lift should not flicker the selection.
      if (x >= rect.left - 6 && x <= rect.right + 6 && y >= rect.top - 8 && y <= rect.bottom + 8) nearby = card;
    }
    return nearby;
  };
  const queueTouchLift = () => {
    if (touchFrame || (!activeTouch && !scrollTouch)) return;
    touchFrame = requestAnimationFrame(() => {
      touchFrame = 0;
      const sample = activeTouch || scrollTouch;
      if (sample && (sample.moved || sample.allowLift)) markLift(slotAt(sample));
      if (activeTouch || (scrollTouch && performance.now() < scrollUntil)) queueTouchLift();
      else scrollTouch = null;
    });
  };
  document.addEventListener('pointerdown', event => {
    if (!event.isPrimary || event.pointerType === 'mouse') return;
    const card = event.target.closest('plndr-record.bm-card');
    markLift(card && !cardControl(event.target) ? card : null);
  }, { capture: true, passive: true });
  document.addEventListener('touchstart', event => {
    stopTouchTracking();
    if (event.touches.length !== 1) return;
    const touch = event.touches[0];
    const card = event.target.closest('plndr-record.bm-card');
    const list = card?.closest('ul') || event.target.closest('.collection-list');
    const allowLift = !!card && !cardControl(event.target);
    markLift(allowLift ? card : null);
    if (!list) return;
    activeTouch = { id: touch.identifier, x: touch.clientX, y: touch.clientY, startX: touch.clientX, startY: touch.clientY, list, allowLift, moved: false };
    queueTouchLift();
  }, { capture: true, passive: true });
  document.addEventListener('touchmove', event => {
    if (!activeTouch) return;
    const touch = [...event.touches].find(touch => touch.identifier === activeTouch.id);
    if (!touch) return;
    activeTouch.x = touch.clientX; activeTouch.y = touch.clientY;
    activeTouch.moved ||= Math.hypot(touch.clientX - activeTouch.startX, touch.clientY - activeTouch.startY) > 8;
    queueTouchLift();
  }, { capture: true, passive: true });
  const finishTouch = event => {
    if (!activeTouch || [...event.touches].some(touch => touch.identifier === activeTouch.id)) return;
    if (activeTouch.moved) {
      scrollTouch = activeTouch;
      scrollUntil = performance.now() + 180;
      markLift(slotAt(scrollTouch));
    }
    activeTouch = null;
    queueTouchLift();
  };
  document.addEventListener('touchend', finishTouch, { passive: true });
  document.addEventListener('touchcancel', finishTouch, { passive: true });
  const onTouchScroll = () => {
    if (activeTouch) activeTouch.moved = true;
    if (scrollTouch) scrollUntil = performance.now() + 140;
    queueTouchLift();
  };
  window.addEventListener('scroll', onTouchScroll, { capture: true, passive: true });
  window.visualViewport?.addEventListener('scroll', onTouchScroll, { passive: true });
  document.addEventListener('visibilitychange', () => { if (document.hidden) stopTouchTracking(); });
  document.addEventListener('plndr:filter-start', () => { stopTouchTracking(); liftCard(null); });
  class Component extends HTMLElement {
    start() { this.abort?.abort(); this.abort = new AbortController(); return this.abort.signal; }
    disconnectedCallback() { this.abort?.abort(); this.observer?.disconnect(); this.resizeObserver?.disconnect(); cancelAnimationFrame(this.frame); }
  }
  class Record extends Component {
    connectedCallback() {
      const signal = this.start();
      if (this.matches('plndr-record.bm-card')) this.setupLift(signal);
      if (this.dataset.resolve !== 'true' || reduced.matches) return;
      this.originals = new Map();
      this.querySelectorAll('[data-bm-resolve]').forEach(el => {
        if (el.childElementCount) {
          const visual = el.querySelector('[data-bm-visual]');
          if (visual) this.originals.set(visual, visual.textContent);
          return;
        }
        const value = el.textContent;
        if (el.getAttribute('aria-hidden') === 'true') this.originals.set(el, value);
        else {
          const stable = document.createElement('span'); stable.className = 'sr-only'; stable.textContent = value;
          const visual = document.createElement('span'); visual.setAttribute('aria-hidden', 'true'); visual.dataset.bmVisual = 'true'; visual.textContent = value;
          el.replaceChildren(stable, visual); this.originals.set(visual, value);
        }
      });
      const restore = () => { cancelAnimationFrame(this.frame); this.originals.forEach((v, el) => el.textContent = v); };
      this.restore = restore;
      const animate = (duration) => {
        if (reduced.matches || document.hidden) return restore();
        cancelAnimationFrame(this.frame); const start = performance.now();
        const tick = now => {
          const progress = Math.min(1, (now - start) / duration);
          this.originals.forEach((value, el) => {
            el.textContent = resolveText(value, progress);
          });
          if (progress < 1) this.frame = requestAnimationFrame(tick); else restore();
        };
        this.frame = requestAnimationFrame(tick);
      };
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => entries.forEach(e => {
          if (e.isIntersecting && !this.dataset.resolved) {
            this.dataset.resolved = 'true'; animate(450);
          }
          if (!e.isIntersecting) restore();
        }), { threshold: .15 }); this.observer.observe(this);
      }
      let last = 0;
      const hover = () => { if (performance.now() - last > 1000) { last = performance.now(); animate(180); } };
      listen(this, 'pointerenter', e => { if (e.pointerType === 'mouse') hover(); }, signal);
      listen(this, 'focusin', hover, signal);
      listen(reduced, 'change', restore, signal);
      listen(document, 'visibilitychange', () => { if (document.hidden) restore(); }, signal);
    }
    setupLift(signal) {
      let touchInput = false;
      listen(this, 'pointerenter', event => { if (event.pointerType === 'mouse') this.removeAttribute('data-bm-touch'); }, signal);
      listen(this, 'pointerdown', event => {
        touchInput = event.pointerType !== 'mouse';
        this.toggleAttribute('data-bm-touch', touchInput);
      }, signal);
      listen(this, 'click', event => {
        if (event.detail === 0 || !touchInput || cardControl(event.target)) return;
        // Image/title links navigate normally. A normal tap on the paper or
        // folder edge opens that same product without adding another control.
        if (!event.target.closest('[data-bm-card-surface]')) this.querySelector('a[data-bm-card-surface]')?.click();
      }, signal);
    }
    disconnectedCallback() { if (liftedCard === this) liftCard(null); this.restore?.(); super.disconnectedCallback(); }
  }
  define('plndr-record', Record);
  define('plndr-barcode', class extends Component {
    connectedCallback() {
      this.start();
      if (!window.JsBarcode || !this.dataset.value || this.querySelector('svg')) return;
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      try {
        window.JsBarcode(svg, this.dataset.value, { format: 'CODE128', displayValue: false, margin: this.classList.contains('bm-barcode') ? 0 : 12, marginLeft: 12, marginRight: 12, height: 28, width: 2, background: this.classList.contains('bm-barcode') ? 'transparent' : '#f2f2eb', lineColor: '#121212' });
        svg.setAttribute('aria-hidden', 'true'); svg.setAttribute('preserveAspectRatio', 'none'); this.append(svg);
        if (this.classList.contains('bm-barcode')) { this.resizeObserver = new ResizeObserver(() => this.style.setProperty('--bm-barcode-length', this.clientHeight + 'px')); this.resizeObserver.observe(this); }
      } catch { /* The readable serial remains the fallback for unsupported characters. */ }
    }
  });
  // Start with the large display type and fit unusually long titles inside the glass.
  define('plndr-controller-screen', class extends Component {
    connectedCallback() {
      const signal = this.start();
      const inkContext = document.createElement('canvas').getContext('2d');
      // Use the font's painted letter bounds, not the differently sized line boxes.
      const inkBounds = el => {
        const style = getComputedStyle(el);
        inkContext.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        const value = style.textTransform === 'uppercase' ? el.textContent.trim().toUpperCase() : el.textContent.trim();
        const metrics = inkContext.measureText(value);
        const firstGlyph = inkContext.measureText([...value][0] || '');
        const rect = el.getBoundingClientRect();
        const lineHeight = parseFloat(style.lineHeight);
        const ascent = metrics.fontBoundingBoxAscent;
        const descent = metrics.fontBoundingBoxDescent;
        const baseline = rect.top + (lineHeight - ascent - descent) / 2 + ascent;
        const range = document.createRange();
        range.selectNodeContents(el);
        const lines = new Set([...range.getClientRects()].map(line => Math.round(line.top))).size || 1;
        return { left: rect.left - firstGlyph.actualBoundingBoxLeft,
          top: baseline - metrics.actualBoundingBoxAscent,
          bottom: baseline + metrics.actualBoundingBoxDescent + (lines - 1) * lineHeight };
      };
      const fit = () => {
        if (!this.isConnected || !this.clientWidth || this.priceAnimation?.playState === 'running') return;
        const title = this.querySelector('h1');
        const alignedText = this.querySelectorAll('.bm-controller-credit,h1,.bm-controller-price-row strong');
        for (const el of alignedText) el.style.setProperty('--bm-ink-left', '0px');
        title.style.setProperty('--bm-title-offset', '0px');
        for (const el of this.querySelectorAll('.bm-controller-credit, h1')) {
          el.style.removeProperty('font-size');
          let size = parseFloat(getComputedStyle(el).fontSize);
          for (let i = 0; i < 40 && (el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1); i++) {
            size *= .97;
            el.style.fontSize = size + 'px';
          }
        }
        if (inkContext) {
          const left = this.getBoundingClientRect().left;
          for (const el of alignedText) {
            const offset = left - inkBounds(el).left;
            if (Number.isFinite(offset)) el.style.setProperty('--bm-ink-left', offset + 'px');
          }
          const credit = inkBounds(this.querySelector('.bm-controller-credit'));
          const price = inkBounds(this.querySelector('.bm-controller-price-row strong'));
          const heading = inkBounds(title);
          const offset = (credit.bottom + price.top - heading.top - heading.bottom) / 2;
          if (Number.isFinite(offset)) title.style.setProperty('--bm-title-offset', offset + 'px');
        }
      };
      this.textObserver?.disconnect();
      this.textObserver = new MutationObserver(fit);
      this.textObserver.observe(this, {childList:true, characterData:true, subtree:true});
      const price = this.querySelector('.bm-controller-price-row strong');
      let previousPrice = price?.textContent.trim();
      this.priceObserver?.disconnect();
      if (price) {
        this.priceObserver = new MutationObserver(() => {
          const next = price.textContent.trim();
          if (next === previousPrice) return;
          previousPrice = next;
          this.priceAnimation?.cancel();
          fit();
          this.priceAnimation = animateScreenChange(price);
          this.priceAnimation?.finished.then(() => { if (this.isConnected) fit(); }).catch(() => {});
        });
        this.priceObserver.observe(price, { childList: true, characterData: true, subtree: true });
      }
      this.resizeObserver = new ResizeObserver(fit);
      this.resizeObserver.observe(this);
      document.fonts?.ready.then(fit);
      fit();
      if (!this.dataset.warmed) {
        this.observer = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.dataset.warmed = 'true';
          if (!reduced.matches) this.classList.add('is-warming');
          this.observer.disconnect();
        }, { threshold: .15 });
        this.observer.observe(this);
      }
      listen(this, 'animationend', () => this.classList.remove('is-warming'), signal);
      const glass = this.parentElement.querySelector('.bm-controller-reflection');
      const device = this.parentElement;
      let touching = false;
      const clear = () => {
        clearTimeout(this.touchReleaseTimer);
        cancelAnimationFrame(this.frame);
        glass.classList.remove('is-reflecting');
        this.classList.remove('is-screen-reacting');
      };
      const reflect = event => {
        if (event.isPrimary === false) return;
        if (reduced.matches || (event.pointerType !== 'mouse' && !touching)) return clear();
        const rect = glass.getBoundingClientRect();
        const x = event.clientX - rect.left, y = event.clientY - rect.top;
        if (x < 0 || y < 0 || x > rect.width || y > rect.height) return clear();
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => {
          glass.style.setProperty('--reflection-x', (x / rect.width * 100) + '%');
          glass.style.setProperty('--reflection-y', (y / rect.height * 100) + '%');
          glass.classList.add('is-reflecting');
          this.classList.add('is-screen-reacting');
        });
      };
      listen(device, 'pointerdown', event => {
        if (!event.isPrimary) return;
        clearTimeout(this.touchReleaseTimer);
        touching = event.pointerType !== 'mouse';
        reflect(event);
      }, signal, { passive: true });
      listen(device, 'pointermove', reflect, signal, { passive: true });
      // Never capture or cancel a touch: the browser keeps normal page scrolling.
      const release = event => {
        if (event.pointerType === 'mouse') return;
        touching = false;
        clearTimeout(this.touchReleaseTimer);
        // Keep a quick tap visible for a beat, then let the reflection fade.
        this.touchReleaseTimer = setTimeout(clear, 180);
      };
      listen(window, 'pointerup', release, signal, { passive: true });
      listen(window, 'pointercancel', () => { touching = false; clear(); }, signal, { passive: true });
      listen(device, 'pointerleave', event => { if (event.pointerType === 'mouse' || touching) clear(); }, signal);
      listen(window, 'blur', () => { touching = false; clear(); }, signal);
      listen(reduced, 'change', () => { clear(); this.classList.remove('is-warming'); }, signal);
    }
    disconnectedCallback() { clearTimeout(this.touchReleaseTimer); this.priceObserver?.disconnect(); this.priceAnimation?.cancel(); this.textObserver?.disconnect(); super.disconnectedCallback(); }
  });
  // Fit only whole summary lines inside the product paper's remaining print area.
  define('plndr-product-paper', class extends Component {
    connectedCallback() {
      this.start();
      const sheet = this.querySelector('.bm-paper-receipt');
      const region = this.querySelector('.bm-paper-description');
      const text = region?.querySelector('p');
      if (!sheet || !text) return;
      const fit = () => {
        if (!this.isConnected) return;
        const height = region.getBoundingClientRect().bottom - text.getBoundingClientRect().top;
        const lineHeight = parseFloat(getComputedStyle(text).lineHeight);
        if (height <= 0 || !lineHeight) return;
        const lines = String(Math.max(1, Math.min(5, Math.floor((height - 1) / lineHeight))));
        if (text.style.getPropertyValue('-webkit-line-clamp') !== lines) text.style.setProperty('-webkit-line-clamp', lines);
      };
      this.resizeObserver = new ResizeObserver(fit);
      this.resizeObserver.observe(sheet);
      document.fonts?.ready.then(fit);
      fit();
    }
  });
  define('plndr-selected-size', class extends Component {
    connectedCallback() {
      this.start();
      let previous;
      const update = () => {
        const value = (this.dataset.value || '').trim();
        if (value === previous) return;
        const key = value.toUpperCase().replace(/[\s-]/g, '');
        const names = { XS: 'Extra Small', S: 'Small', M: 'Medium', L: 'Large', XL: 'Extra Large', XXL: '2X Large', '2XL': '2X Large', XXXL: '3X Large', '3XL': '3X Large', XXXXL: '4X Large', '4XL': '4X Large' };
        this.textContent = value ? '— ' + (names[key] || value) : '';
        this.sizeAnimation?.cancel();
        if (previous !== undefined && value) this.sizeAnimation = animateScreenChange(this);
        previous = value;
      };
      this.sizeObserver = new MutationObserver(update);
      this.sizeObserver.observe(this, { attributes: true, attributeFilter: ['data-value'] });
      update();
    }
    disconnectedCallback() { this.sizeObserver?.disconnect(); this.sizeAnimation?.cancel(); super.disconnectedCallback(); }
  });
  define('plndr-product', class extends Record {
    connectedCallback() {
      super.connectedCallback();
      const signal = this.abort.signal;
      const tabs = [...this.querySelectorAll('[data-bm-tab]')];
      const panels = [...this.querySelectorAll('[data-bm-panel]')];
      const gallery = this.querySelector('[x-ref="splide"]');
      if (!gallery || !tabs.length) return;
      this.dataset.enhanced = 'true'; this.dataset.activeTab = 'images';
      const prefix = this.querySelector('[data-product-root]').dataset.productRoot;
      gallery.id ||= 'bm-images-' + prefix;
      gallery.setAttribute('role', 'tabpanel'); gallery.setAttribute('aria-label', 'Images');
      this.querySelector('.bm-tabs').setAttribute('role', 'tablist');
      const select = name => {
        this.dataset.activeTab = name;
        tabs.forEach(t => { const active = t.dataset.bmTab === name; t.setAttribute('aria-selected', String(active)); t.tabIndex = active ? 0 : -1; });
        panels.forEach(p => { p.hidden = p.dataset.bmPanel !== name; });
        if (name === 'related') this.querySelector('plndr-related')?.load();
        gallery.setAttribute('aria-hidden', String(name !== 'images'));
        gallery.inert = name !== 'images';
      };
      tabs.forEach((tab, i) => {
        const name = tab.dataset.bmTab; const panel = name === 'images' ? gallery : panels.find(p => p.dataset.bmPanel === name);
        tab.id = 'bm-tab-' + prefix + '-' + name; tab.setAttribute('role', 'tab'); tab.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', 'tabpanel'); panel.setAttribute('aria-labelledby', tab.id); panel.tabIndex = 0;
        listen(tab, 'click', e => { e.preventDefault(); select(name); }, signal);
        listen(tab, 'keydown', e => {
          let next;
          if (e.key === 'ArrowRight') next = (i + 1) % tabs.length;
          if (e.key === 'ArrowLeft') next = (i + tabs.length - 1) % tabs.length;
          if (e.key === 'Home') next = 0;
          if (e.key === 'End') next = tabs.length - 1;
          if (next !== undefined) { e.preventDefault(); select(tabs[next].dataset.bmTab); tabs[next].focus(); }
          if (e.key === ' ') { e.preventDefault(); select(name); }
        }, signal);
      });
      select('images');
      if (this.hasAttribute('data-controller-page')) { this.setupViewportLayout(signal); this.setupHeadingLayout(signal); }
      // The prefix is the same product object ID used on its record and receipt.
      // Keep media positions stable when variants reorder or filter the gallery.
      const imageNumber = gallery.querySelector('[data-bm-image-number-output]');
      const mediaList = gallery.querySelector('.bm-media-list');
      if (imageNumber && mediaList) {
        const updateImageNumber = index => {
          const slides = [...mediaList.children].filter(slide => !slide.classList.contains('splide__slide--clone'));
          const slide = Number.isInteger(index) ? slides[index] : slides.find(slide => slide.classList.contains('is-active')) || slides[0];
          if (!slide) return;
          const position = Number(slide.dataset.bmImageNumber);
          if (!Number.isInteger(position) || position < 1) return;
          const label = imageNumber.dataset.objectId + '-' + String(position).padStart(2, '0');
          if (imageNumber.textContent !== label) imageNumber.textContent = label;
        };
        listen(gallery, 'move', event => updateImageNumber(event.detail?.newIndex), signal);
        this.imageNumberObserver?.disconnect();
        this.imageNumberObserver = new MutationObserver(() => updateImageNumber());
        this.imageNumberObserver.observe(mediaList, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'data-bm-image-number'] });
        updateImageNumber();
      }
      const track = gallery.querySelector('.splide__track');
      const controls = gallery.querySelector('.splide__arrows')?.parentElement;
      if (track && controls && !this.hasAttribute('data-controller-page')) {
        const counter = document.createElement('span'); counter.className = 'bm-image-count';
        counter.setAttribute('aria-live', 'polite'); controls.append(counter);
        const updateMedia = () => {
          const slides = [...track.querySelectorAll('.splide__slide:not(.splide__slide--clone)')];
          const active = slides.findIndex(slide => slide.classList.contains('is-active'));
          counter.textContent = `${Math.max(0, active) + 1}/${slides.length}`;
        };
        this.mediaObserver = new MutationObserver(updateMedia);
        this.mediaObserver.observe(track, { subtree: true, attributes: true, attributeFilter: ['class'] });
        updateMedia();
      }
      listen(this, 'click', e => {
        const link = e.target.closest('a[href^="#bm-"]'); if (!link || link.hasAttribute('data-bm-tab')) return;
        const target = document.getElementById(link.hash.slice(1)); if (!target) return;
        e.preventDefault();
        const targetPanel = target.closest('[data-bm-panel]');
        if (targetPanel) select(targetPanel.dataset.bmPanel);
        if (target.tagName === 'DETAILS') target.open = true;
        target.focus({ preventScroll: true });
        if (targetPanel && this.hasAttribute('data-controller-page')) {
          // Keep the tabs visible under the sticky header, not just the panel body.
          const body = targetPanel.querySelector('.bm-record-body');
          if (body) body.scrollTop = 0;
          if (targetPanel.dataset.bmPanel === 'notes' && link.closest('.bm-product-paper') && matchMedia('(max-width:1059px)').matches) {
            window.scrollTo({ top: 0, behavior: reduced.matches ? 'instant' : 'smooth' });
            return;
          }
          const media = this.querySelector('.product-media-container');
          const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-height')) || 0;
          media.style.scrollMarginTop = (Math.max(0, headerHeight) + 16) + 'px';
          media.scrollIntoView({ behavior: reduced.matches ? 'instant' : 'smooth', block: 'start' });
        } else target.scrollIntoView({ behavior: reduced.matches ? 'instant' : 'smooth', block: 'start' });
      }, signal);
    }
    setupHeadingLayout(signal) {
      const heading = this.querySelector('.bm-product-heading');
      const text = heading?.querySelector('svg text');
      if (!heading || !text) return;
      const canvas = document.createElement('canvas'), context = canvas.getContext('2d');
      const align = () => {
        if (signal.aborted || !this.isConnected) return;
        if (!matchMedia('(min-width:1060px)').matches) {
          heading.style.removeProperty('--bm-logo-align');
          heading.style.removeProperty('--bm-title-ink-top');
          const caption = heading.querySelector('.bm-catalog-stamp p');
          if (caption) caption.style.transform = '';
          return;
        }
        const font = getComputedStyle(text), svg = text.ownerSVGElement;
        context.font = `${font.fontWeight} ${font.fontSize} ${font.fontFamily}`;
        const inset = (Number(text.getAttribute('y')) - context.measureText(text.textContent).actualBoundingBoxAscent) * svg.getBoundingClientRect().height / svg.viewBox.baseVal.height;
        heading.style.setProperty('--bm-title-ink-top', inset.toFixed(2) + 'px');
        const caption = heading.querySelector('.bm-catalog-stamp p');
        if (caption) {
          caption.style.transform = '';
          const firstLine = caption.firstElementChild || caption;
          const probe = document.createElement('span');
          probe.style.cssText = 'display:inline-block;width:0;height:0;padding:0;margin:0;vertical-align:baseline';
          firstLine.prepend(probe);
          const captionFont = getComputedStyle(caption);
          context.font = `${captionFont.fontWeight} ${captionFont.fontSize} ${captionFont.fontFamily}`;
          const captionTop = probe.getBoundingClientRect().top - context.measureText(firstLine.textContent).actualBoundingBoxAscent;
          probe.remove();
          caption.style.transform = `translateY(${(svg.getBoundingClientRect().top + inset - captionTop).toFixed(2)}px)`;
        }
        // Same ink registration used by the catalog masthead beside the sidebar logo.
        const logo = document.querySelector('.gt-sidebar-logo img');
        const sidebar = logo?.closest('.gt-sidebar'), shell = sidebar?.closest('.gt-shell');
        if (!logo || !sidebar || !shell || !logo.getBoundingClientRect().width) return;
        const brand = logo.closest('.gt-sidebar-brand');
        const known = brand && (logo.src.includes(brand.dataset.bundledLogo) || logo.src.includes('terror-logo-night.png') || logo.src.includes('terror-logo-dark-original.png'));
        const inkInset = known ? logo.getBoundingClientRect().height * 22 / 258 : 0;
        const logoTop = shell.getBoundingClientRect().top + (parseFloat(getComputedStyle(shell).paddingTop) || 0) + logo.getBoundingClientRect().top - sidebar.getBoundingClientRect().top + sidebar.scrollTop + inkInset;
        const next = Math.max(0, logoTop - heading.getBoundingClientRect().top - inset);
        const current = parseFloat(getComputedStyle(heading).paddingTop) || 0;
        if (Math.abs(next - current) > .25) heading.style.setProperty('--bm-logo-align', next.toFixed(2) + 'px');
      };
      const schedule = () => { cancelAnimationFrame(this.headingFrame); this.headingFrame = requestAnimationFrame(align); };
      this.headingObserver = new ResizeObserver(schedule);
      this.headingObserver.observe(heading);
      const logo = document.querySelector('.gt-sidebar-logo img');
      if (logo) { this.headingObserver.observe(logo); listen(logo, 'load', schedule, signal); }
      listen(window, 'resize', schedule, signal, { passive: true });
      listen(document, 'terror:layout-ready', schedule, signal);
      document.fonts?.ready.then(() => { if (!signal.aborted) schedule(); });
      schedule();
    }
    setupViewportLayout(signal) {
const root=this.querySelector('[data-product-root]'),media=this.querySelector('.product-media-container'),purchase=this.querySelector('.product-content-container'),series=this.querySelector('.bm-product-series'),board=purchase?.querySelector('.bm-product-backboard');
if(!root||!media||!purchase||!series||!board)return;
let fittedWidth;
const fit=()=>{if(!this.isConnected||signal.aborted||!root.clientWidth)return;const rootWidth=root.getBoundingClientRect().width;fittedWidth=rootWidth;const columns=getComputedStyle(root);if(columns.gridTemplateColumns.split(' ').length<2){root.style.removeProperty('--bm-purchase-width');purchase.style.removeProperty('--bm-purchase-top');return;}const width=rootWidth-(parseFloat(columns.columnGap)||0),slope=.098+1337/1366+300/2128+1444/1500.8+1162/1086-.008;for(let pass=0;pass<5;pass++){purchase.style.setProperty('--bm-purchase-top',(media.getBoundingClientRect().width*.018).toFixed(3)+'px');const right=purchase.getBoundingClientRect(),difference=series.getBoundingClientRect().bottom-board.getBoundingClientRect().bottom;if(Math.abs(difference)<.35)break;const next=Math.max(width*.28,Math.min(width*.48,right.width+difference/slope));if(Math.abs(next-right.width)<.02)break;root.style.setProperty('--bm-purchase-width',next.toFixed(3)+'px');}};
const schedule=()=>{cancelAnimationFrame(this.layoutFrame);this.layoutFrame=requestAnimationFrame(fit);};this.layoutObserver?.disconnect();this.layoutObserver=new ResizeObserver(schedule);this.layoutObserver.observe(root);listen(window,'resize',schedule,signal,{passive:true});listen(document,'terror:layout-ready',()=>{fittedWidth=undefined;schedule();},signal);document.fonts?.ready.then(()=>{if(!signal.aborted){fittedWidth=undefined;schedule();}});schedule();
}
    disconnectedCallback() { cancelAnimationFrame(this.headingFrame); this.headingObserver?.disconnect(); cancelAnimationFrame(this.layoutFrame); this.layoutObserver?.disconnect(); this.imageNumberObserver?.disconnect(); this.mediaObserver?.disconnect(); this.querySelector('.bm-image-count')?.remove(); super.disconnectedCallback(); }
  });
  define('plndr-related', class extends Component {
    connectedCallback() {
      this.start();
      if (this.hasAttribute('data-auto-load')) {
        this.observer = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.observer.disconnect(); this.load();
        }, { rootMargin: '300px' });
        this.observer.observe(this);
      }
    }
    async load() {
      if (this.loaded || this.loading) return;
      this.loading = true;
      const status = this.querySelector('[data-bm-related-status]'); status.textContent = 'Loading related objects…';
      try {
        const response = await fetch(this.dataset.url, { signal: this.abort.signal }); if (!response.ok) throw new Error('Recommendations unavailable');
        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = parsed.querySelector('.related-products');
        if (content?.querySelector('a[href*="/products/"]')) { this.querySelector('[data-bm-related-empty]')?.remove(); this.replaceChildren(content); }
        else { status.textContent = 'No related objects are filed for this record yet.'; this.querySelector('[data-bm-related-empty]').hidden = false; }
        this.loaded = true;
      } catch (e) {
        if (e.name !== 'AbortError') {
          status.textContent = 'Related objects could not be loaded. ';
          const retry = document.createElement('button'); retry.type = 'button'; retry.textContent = 'Try again'; retry.onclick = () => this.load(); status.append(retry);
        }
      } finally { this.loading = false; }
    }
  });
  define('plndr-fulfillment', class extends Component {
    connectedCallback() {
      const signal = this.start();
      const track = this.querySelector('.bm-crt-track');
      if (!track) return;
      const unit = track.querySelector('.bm-crt-unit');
      const screen = this.querySelector('.bm-crt-window');
      const measure = () => {
        track.querySelectorAll('[data-bm-repeat]').forEach(el => el.remove());
        const width = unit.getBoundingClientRect().width;
        if (!width) return;
        const count = Math.ceil(screen.clientWidth / width) + 1;
        for (let i = 0; i < count; i++) {
          const clone = unit.cloneNode(true); clone.dataset.bmRepeat = ''; clone.setAttribute('aria-hidden', 'true'); track.append(clone);
        }
        this.style.setProperty('--bm-ticker-distance', width + 'px');
        // The existing cycle setting controls speed consistently, independent of label length.
        const pixelsPerSecond = 840 / (Number(this.dataset.duration) || 24);
        this.style.setProperty('--bm-ticker-duration', width / pixelsPerSecond + 's');
      };
      this.resizeObserver = new ResizeObserver(measure); this.resizeObserver.observe(screen); this.resizeObserver.observe(unit);
      this.observer = new IntersectionObserver(entries => entries.forEach(e => this.toggleAttribute('data-visible', e.isIntersecting && !document.hidden)));
      this.observer.observe(this);
      listen(document, 'visibilitychange', () => this.toggleAttribute('data-visible', !document.hidden && this.getBoundingClientRect().bottom > 0 && this.getBoundingClientRect().top < innerHeight), signal);
      document.fonts.ready.then(() => { if (this.isConnected) measure(); });
      measure();
    }
  });
  define('plndr-categories', class extends Component {
    connectedCallback() {
      const signal = this.start();
      this.section = this.closest('.shopify-section') || this.parentElement;
      this.nav = this.querySelector('nav');
      this.navSlot = this.querySelector('.bm-category-slot');
      this.status = this.querySelector('.bm-filter-status');
      this.recordCount = this.querySelector('[data-bm-record-count]');
      this.totalCount = Number(this.recordCount?.dataset.totalCount) || 0;
      this.nextUrl = this.dataset.nextUrl;
      this.overlays = new Set();
      this.category = 'all';
      const catalogPath = new URL(this.dataset.catalogUrl || '/collections/all', location.href).pathname;
      this.local = location.pathname.replace(/\/$/, '') === catalogPath.replace(/\/$/, '');
      this.links = [...this.querySelectorAll('[data-bm-category]')];
      this.setupHardwareMenu(signal);
      const apply = (category, announce = true) => {
        this.category = category;
        let count = 0;
        this.section.querySelectorAll('plndr-record[data-categories]').forEach(record => {
          const categories = record.dataset.categories.split(/[,\s]+/).filter(Boolean);
          const visible = category === 'all' || categories.includes(category);
          record.closest('li').classList.toggle('bm-filter-hidden', !visible);
          if (visible) count++;
        });
        this.links.forEach(link => { if (link.dataset.bmCategory === category) link.setAttribute('aria-current', 'page'); else link.removeAttribute('aria-current'); });
        if (this.recordCount) {
          const shownCount = category === 'all' ? Math.max(count, this.totalCount) : count;
          const pending = category !== 'all' && this.nextUrl ? '+' : '';
          this.updateRecordCount(`${shownCount}${pending} ${shownCount === 1 ? 'RECORD' : 'RECORDS'}`, announce);
        }
        this.dispatchEvent(new CustomEvent('plndr:catalog-filtered', { bubbles: true }));
        this.status.removeAttribute('data-error');
        this.status.hidden = !announce && category === 'all';
        this.status.textContent = count ? `${count} ${count === 1 ? 'RECORD' : 'RECORDS'} // ${category.toUpperCase()}` : `No records filed under ${category.toUpperCase()}.`;
      };
      this.apply = apply;
      const fromHash = (animate = true) => {
        const category = location.hash.replace('#bm-category-', '');
        if (!this.local) return;
        if (this.links.some(link => link.dataset.bmCategory === category)) {
          this.filter(category, animate);
          if (category === 'media' || category === 'archive') this.setMenuPage?.(1);
        } else if (!location.hash) this.filter('all', animate);
      };
      listen(this.nav, 'click', event => {
        const link = event.target.closest('[data-bm-category]');
        if (!link || !this.local || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        history.replaceState(null, '', link.href);
        this.filter(link.dataset.bmCategory);
      }, signal);
      listen(window, 'hashchange', () => fromHash(), signal);
      fromHash(false);
      listen(reduced, 'change', () => { this.restoreCount?.(); if (reduced.matches) this.transitionRecords(this.category, false); }, signal);
      listen(document, 'visibilitychange', () => { if (document.hidden) this.restoreCount?.(); }, signal);
      const queue = () => { if (!this.frame) this.frame = requestAnimationFrame(() => this.update()); };
      this.queue = queue;
      listen(window, 'scroll', queue, signal, { passive: true });
      listen(window, 'resize', queue, signal, { passive: true });
      listen(window.visualViewport || window, 'resize', queue, signal, { passive: true });
      listen(document, 'terror:overlay-state', event => { event.detail?.open ? this.overlays.add(event.detail.source) : this.overlays.delete(event.detail?.source); queue(); }, signal);
      listen(document, 'theme:cart-drawer:opening', () => { this.overlays.add('cart'); queue(); }, signal);
      listen(document, 'theme:cart-drawer:closed', () => { this.overlays.delete('cart'); queue(); }, signal);
      for (const name of ['right-drawer', 'left-drawer', 'quick-buy-drawer']) {
        listen(document, name + '-will-open', () => { this.overlays.add(name); queue(); }, signal);
        listen(document, name + '-is-closed', () => { this.overlays.delete(name); queue(); }, signal);
      }
      listen(document, 'terror:device-ready', queue, signal);
      this.resizeObserver = new ResizeObserver(() => { this.syncMenuPanels?.(); queue(); }); this.resizeObserver.observe(this.nav);
      this.update();
    }
    updateRecordCount(value, animate) {
      const host = this.recordCount;
      const previous = host.dataset.bmCountValue ?? host.textContent.trim();
      host.dataset.bmCountValue = value;
      this.restoreCount?.();
      const stable = document.createElement('span'); stable.className = 'sr-only'; stable.textContent = value;
      const visual = document.createElement('span'); visual.setAttribute('aria-hidden', 'true'); visual.dataset.bmCountVisual = ''; visual.textContent = value;
      host.replaceChildren(stable, visual);
      this.restoreCount = () => { cancelAnimationFrame(this.countFrame); visual.textContent = value; host.removeAttribute('data-bm-count-resolving'); };
      if (!animate || previous === value || reduced.matches || document.hidden) return;
      host.setAttribute('data-bm-count-resolving', '');
      const start = performance.now();
      const tick = now => {
        const progress = Math.min(1, (now - start) / 180);
        visual.textContent = resolveText(value, progress);
        if (progress < 1) this.countFrame = requestAnimationFrame(tick); else this.restoreCount();
      };
      this.countFrame = requestAnimationFrame(tick);
    }
    setupHardwareMenu(signal) {
      const slider = this.querySelector('[data-bm-menu-slider]');
      if (!slider) return;
      const panels = [...this.querySelectorAll('[data-bm-menu-panel]')];
      this.menuPage = 0;
      let pointerClick = false;
      this.syncMenuPanels = () => {
        const desktop = getComputedStyle(panels[0]).display !== 'none';
        panels.forEach(panel => {
          const active = desktop ? panel.dataset.bmMenuPanel === 'desktop' : panel.dataset.bmMenuPanel === (this.menuPage ? 'end' : 'start');
          panel.inert = !active;
          panel.setAttribute('aria-hidden', String(!active));
        });
      };
      this.setMenuPage = (page, focusArrow = false) => {
        this.menuPage = page;
        slider.dataset.page = String(page);
        this.syncMenuPanels();
        if (focusArrow) this.querySelector(`[data-bm-menu-panel="${page ? 'end' : 'start'}"] [data-bm-menu-page]`)?.focus({ preventScroll: true });
      };
      listen(this.nav, 'click', event => {
        const arrow = event.target.closest('[data-bm-menu-page]');
        if (!arrow) return;
        // Transfer focus only for keyboard activation, never for a tap.
        this.setMenuPage(Number(arrow.dataset.bmMenuPage), !pointerClick && event.detail === 0 && arrow.matches(':focus-visible'));
      }, signal);
      // Space activates category links just like physical buttons; Enter works natively.
      listen(this.nav, 'keydown', event => {
        const category = event.target.closest('[data-bm-category]');
        if (category && event.key === ' ') { event.preventDefault(); category.click(); }
      }, signal);
      this.setMenuPage(0);
      let contact = null;
      let consumed = null;
      const release = () => { contact?.key.removeAttribute('data-bm-pressed'); contact = null; };
      listen(this.nav, 'pointerdown', event => {
        if (event.pointerType === 'mouse' || !event.isPrimary) return;
        const key = event.target.closest('.bm-hardware-key');
        if (!key) return;
        release();
        contact = { key, id: event.pointerId, x: event.clientX, y: event.clientY };
        key.setAttribute('data-bm-pressed', '');
      }, signal);
      listen(this.nav, 'pointermove', event => {
        if (contact && Math.hypot(event.clientX - contact.x, event.clientY - contact.y) > 10) { consumed = { key: contact.key, until: performance.now() + 700 }; release(); }
      }, signal, { passive: true });
      listen(this.nav, 'pointercancel', release, signal);
      listen(document, 'pointerup', event => {
        if (!contact) return;
        const { key, id, x, y } = contact;
        release();
        if (id !== event.pointerId || !key.contains(event.target) || Math.hypot(event.clientX - x, event.clientY - y) > 10) return;
        // Trigger the existing link/button handler now, bypassing iOS hover emulation.
        consumed = { key, until: performance.now() + 700 };
        pointerClick = true;
        try { key.click(); } finally { pointerClick = false; }
      }, signal);
      listen(this.nav, 'click', event => {
        if (event.detail && consumed && performance.now() < consumed.until && consumed.key.contains(event.target)) {
          event.preventDefault(); event.stopImmediatePropagation(); consumed = null;
        }
      }, signal, { capture: true });
    }
    clearFilterFlow() {
      const flow = this.filterFlow;
      if (!flow) return;
      this.section.style.height = flow.height;
      flow.tails.forEach(({ inert }, element) => {
        element.inert = inert;
        element.removeAttribute('data-bm-flowing');
      });
      flow.root.removeAttribute('data-bm-flow-root');
      flow.root.style.removeProperty('--bm-flow-min-height');
      this.filterFlow = null;
    }
    filterFollowingContent(list, root) {
      const targets = [];
      // Follow document flow through the collection wrappers, later sections,
      // and the footer group. The adjacent desktop sidebar stays in place.
      for (let node = list; node && node !== root; node = node.parentElement) {
        for (let sibling = node.nextElementSibling; sibling; sibling = sibling.nextElementSibling) {
          if (sibling.matches('script,style,template,.bm-catalog-sidebar,[hidden]')) continue;
          const rect = sibling.getBoundingClientRect();
          const position = getComputedStyle(sibling).position;
          if (rect.height && rect.width && position !== 'fixed' && position !== 'absolute') targets.push(sibling);
        }
      }
      return targets;
    }
    async transitionRecords(category, animate = true) {
      const version = this.filterVersion = (this.filterVersion || 0) + 1;
      // Capture an interrupted flight before canceling so rapid selections never
      // snap a partly moving card back to its resting position.
      const startHeight = this.section.getBoundingClientRect().height;
      const interruptedFlow = new Map();
      this.filterFlow?.tails.forEach((_, element) => interruptedFlow.set(element, element.getBoundingClientRect()));
      const interrupted = new Map();
      this.filterAnimations?.forEach(animation => {
        const target = animation.effect?.target;
        if (target?.tagName === 'LI') interrupted.set(target, getComputedStyle(target).transform);
      });
      this.filterAnimations?.forEach(animation => animation.cancel());
      this.filterAnimations = new Set();
      this.clearFilterFlow();
      const list = this.section.querySelector('.collection-list');
      if (!list) { this.apply(category); return; }
      const items = [...list.querySelectorAll(':scope > li:has(plndr-record)')];
      items.forEach(item => { item.inert = false; });
      list.removeAttribute('data-bm-filtering');
      list.style.height = '';
      this.category = category;
      this.links.forEach(link => { if (link.dataset.bmCategory === category) link.setAttribute('aria-current', 'page'); else link.removeAttribute('aria-current'); });
      document.dispatchEvent(new Event('plndr:filter-start'));
      const moving = animate && !reduced.matches && typeof list.animate === 'function';
      if (!moving) { this.apply(category, animate); return; }
      const root = this.section.closest('.gt-main-column') || this.section.closest('main')?.parentElement || this.section.parentElement;
      const tails = new Map(this.filterFollowingContent(list, root).map(element => [element, {
        inert: element.inert, rect: interruptedFlow.get(element) || element.getBoundingClientRect()
      }]));
      this.filterFlow = { root, tails, height: this.section.style.height };
      // Keep flow stable during the exit, then ease the whole section's height.
      // Clipping at the column boundary prevents moving footer blocks from
      // creating temporary scrollbars, while allowing a full viewport of travel.
      root.style.setProperty('--bm-flow-min-height', Math.max(0, innerHeight - root.getBoundingClientRect().top) + 'px');
      root.setAttribute('data-bm-flow-root', '');
      this.section.style.height = startHeight + 'px';
      tails.forEach((_, element) => { element.inert = true; element.setAttribute('data-bm-flowing', ''); });
      const clipBounds = list.getBoundingClientRect();
      const clipStyle = getComputedStyle(list);
      // Safari lacks overflow-clip-margin. Preserve the grid's content width
      // while extending its clipping box to the viewport during a flight.
      list.style.setProperty('--bm-flight-left', clipBounds.left + 'px');
      list.style.setProperty('--bm-flight-right', (innerWidth - clipBounds.right) + 'px');
      list.style.setProperty('--bm-flight-pad-left', clipStyle.paddingLeft);
      list.style.setProperty('--bm-flight-pad-right', clipStyle.paddingRight);
      list.style.setProperty('--bm-flight-space', Math.max(0, innerHeight - clipBounds.bottom) + 'px');
      list.setAttribute('data-bm-filtering', '');
      const mobile = innerWidth < (Number(this.dataset.breakpoint) || 1060);
      const center = clipBounds.left + clipBounds.width / 2;
      const inView = item => { const rect = item.getBoundingClientRect(); return rect.bottom > -80 && rect.top < innerHeight + 80; };
      const variation = item => {
        const id = item.querySelector('[data-record-id]')?.dataset.recordId || '0';
        let hash = version * 17;
        for (const char of id) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619);
        hash = Math.imul(hash ^ (hash >>> 16), 2246822507);
        return (hash >>> 0) / 4294967295;
      };
      const play = (element, frames, options) => {
        const animation = element.animate(frames, options);
        this.filterAnimations.add(animation);
        return animation.finished.catch(() => {});
      };
      const visible = items.filter(item => !item.classList.contains('bm-filter-hidden'));
      visible.forEach(item => { item.inert = true; });
      const exits = visible.filter(inView).map((item, index) => {
        const rect = item.getBoundingClientRect();
        const seed = variation(item);
        const left = rect.left + rect.width / 2 < center;
        const x = mobile ? (left ? -rect.right - 36 : innerWidth - rect.left + 36) : (seed - .5) * 28;
        const y = mobile ? (seed - .5) * 32 : innerHeight - rect.top + 48;
        const angle = (mobile ? (left ? -1 : 1) : (seed < .5 ? -1 : 1)) * (1.5 + seed * 2.5);
        return play(item, [
          { transform: interrupted.get(item) || 'translate3d(0,0,0) rotate(0deg)' },
          { transform: `translate3d(${x}px,${y}px,0) rotate(${angle}deg)` }
        ], { duration: 265 + seed * 65, delay: Math.min(index * 12, 36) + seed * 25, easing: 'cubic-bezier(.5,.04,.78,.4)', fill: 'both' });
      });
      tails.forEach((state, element) => {
        const rect = element.getBoundingClientRect();
        const from = state.rect.top - rect.top;
        const to = Math.max(from, innerHeight - rect.top + 36);
        state.exitTop = rect.top + to;
        if ((state.rect.top < innerHeight && state.rect.bottom > 0) || from !== 0) {
          exits.push(play(element, [{ translate: `0 ${from}px` }, { translate: `0 ${to}px` }], {
            duration: 330, easing: 'cubic-bezier(.5,.04,.78,.4)', fill: 'both'
          }));
        }
      });
      await Promise.all(exits);
      if (version !== this.filterVersion || !this.isConnected) return;
      this.filterAnimations.forEach(animation => animation.cancel());
      this.filterAnimations.clear();
      this.apply(category);
      list.style.setProperty('--bm-flight-space', '0px');
      list.style.setProperty('--bm-flight-space', Math.max(0, innerHeight - list.getBoundingClientRect().bottom) + 'px');
      // Measure the resting layout without Safari's expanded flight box.
      // Counting that temporary area keeps the footer below the viewport,
      // then makes it jump upward when the clipping fallback is removed.
      list.removeAttribute('data-bm-filtering');
      this.section.style.height = this.filterFlow.height;
      const newHeight = this.section.getBoundingClientRect().height;
      const restingTops = new Map([...tails.keys()].map(element => [element, element.getBoundingClientRect().top]));
      this.section.style.height = startHeight + 'px';
      list.setAttribute('data-bm-filtering', '');
      const flowTiming = { duration: 540, easing: 'cubic-bezier(.22,.72,.2,1)', fill: 'both' };
      const flowJobs = [];
      if (startHeight !== newHeight) flowJobs.push(play(this.section, [{ height: startHeight + 'px' }, { height: newHeight + 'px' }], flowTiming));
      tails.forEach((state, element) => {
        const rect = element.getBoundingClientRect();
        // Both ends are below the screen when the new catalog is long. When
        // it is empty/short, this same path brings the footer up with easing.
        const offset = Math.max(innerHeight + 36, state.exitTop) - rect.top;
        const heightShift = this.section.contains(element) ? 0 : newHeight - startHeight;
        const restingOffset = restingTops.get(element) - rect.top - heightShift;
        flowJobs.push(play(element, [{ translate: `0 ${offset}px` }, { translate: `0 ${restingOffset}px` }], flowTiming));
      });
      const entering = items.filter(item => !item.classList.contains('bm-filter-hidden') && inView(item));
      const jobs = entering.map((item, index) => {
        item.inert = true;
        const rect = item.getBoundingClientRect();
        const seed = variation(item);
        return play(item, [
          { transform: `translate3d(${(seed - .5) * 24}px,${innerHeight - rect.top + 48}px,0) rotate(${(seed - .5) * 6}deg)`, offset: 0, easing: 'cubic-bezier(.16,.76,.23,1)' },
          { transform: `translate3d(0,-4px,0) rotate(${(seed - .5) * .5}deg)`, offset: .86, easing: 'ease-out' },
          { transform: 'translate3d(0,0,0) rotate(0deg)', offset: 1 }
        ], { duration: 410 + seed * 90, delay: Math.min(index * 16, 55) + seed * 30, fill: 'both' });
      });
      await Promise.all([...jobs, ...flowJobs]);
      if (version !== this.filterVersion) return;
      this.filterAnimations.forEach(animation => animation.cancel());
      this.filterAnimations.clear();
      items.forEach(item => { item.inert = false; });
      list.removeAttribute('data-bm-filtering');
      this.clearFilterFlow();
    }
    async filter(category, animate = true) {
      const transition = this.transitionRecords(category, animate);
      if (category === 'all' || !this.nextUrl) return transition;
      if (this.loading) return transition;
      this.loading = true;
      await transition;
      this.status.textContent = 'Loading the remaining records…';
      try {
        while (this.nextUrl) {
          const response = await fetch(this.nextUrl, { signal: this.abort.signal });
          if (!response.ok) throw new Error('Catalog unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const next = doc.querySelector('plndr-categories');
          if (!next) throw new Error('Catalog missing');
          const list = this.section.querySelector('.collection-list');
          const known = new Set([...list.querySelectorAll('[data-record-id]')].map(x => x.dataset.recordId));
          next.closest('.shopify-section').querySelectorAll('.collection-list plndr-record').forEach(record => { if (!known.has(record.dataset.recordId)) { const item = record.closest('li'); item.classList.add('bm-filter-hidden'); list.append(item); known.add(record.dataset.recordId); } });
          const nextUrl = next.dataset.nextUrl;
          this.nextUrl = nextUrl && nextUrl !== this.nextUrl ? nextUrl : '';
          await this.transitionRecords(this.category, true);
        }
      } catch (error) {
        if (error.name !== 'AbortError') { this.status.hidden = false; this.status.setAttribute('data-error', ''); this.status.textContent = 'Some records could not be loaded. Select the category again to retry, or use the page links below.'; }
      } finally { this.loading = false; }
    }
    update() {
      this.frame = 0;
      const mobile = innerWidth < (Number(this.dataset.breakpoint) || 1060);
      const dock = this.dataset.floating === 'true' && mobile && scrollY > 12;
      const changed = dock !== this.hasAttribute('data-docked');
      this.toggleAttribute('data-docked', dock);
      this.nav.toggleAttribute('data-bm-docked', dock);
      // Keep the fixed controller out of the page's blur/filter containing block.
      // The original links stay intact, preserving focus and category listeners.
      if (dock && this.nav.parentElement !== document.body) document.body.append(this.nav);
      else if (!dock && this.nav.parentElement !== this.navSlot) this.navSlot.append(this.nav);
      // The shared Device/Terminal dock owns both miniatures; reserve the entire bar.
      const inset = dock ? this.nav.offsetHeight + 12 : 0;
      const root = document.documentElement;
      if (root.style.getPropertyValue('--bm-controller-inset') !== inset + 'px') {
        root.style.setProperty('--bm-controller-inset', inset + 'px');
        document.dispatchEvent(new CustomEvent('plndr:controller-change', { detail: { height: inset, docked: dock } }));
      }
      const deviceOpen = root.dataset.tsActive === 'device' || this.overlays.has('device');
      const otherOverlay = [...this.overlays].some(name => name !== 'device');
      const otherDialog = [...document.querySelectorAll('dialog[open]')].some(dialog => !dialog.classList.contains('dm-dialog'));
      const obstructed = dock && (otherOverlay || otherDialog || (!!root.dataset.tsActive && !deviceOpen));
      this.toggleAttribute('data-obstructed', obstructed);
      this.nav.toggleAttribute('data-bm-obstructed', obstructed);
      this.nav.toggleAttribute('data-bm-device-open', dock && deviceOpen);
      if (this.section) this.section.style.paddingBottom = dock ? `calc(${inset + 20}px + env(safe-area-inset-bottom, 0px))` : '';
      if (changed) {
        this.motion?.cancel();
        if (dock && !reduced.matches && !obstructed) {
          const distance = this.nav.offsetHeight + (parseFloat(getComputedStyle(this.nav).bottom) || 12) + 8;
          this.motion = this.nav.animate([
            { transform: `translateY(${distance}px)`, opacity: 0 },
            { transform: 'translateY(0)', opacity: 1 }
          ], { duration: 280, easing: 'cubic-bezier(.22,.72,.18,1)' });
        }
      }
    }
    disconnectedCallback() {
      this.restoreCount?.();
      this.filterVersion = (this.filterVersion || 0) + 1;
      this.filterAnimations?.forEach(animation => animation.cancel());
      this.clearFilterFlow();
      this.section?.querySelector('.collection-list')?.removeAttribute('data-bm-filtering');
      this.section?.querySelectorAll('.collection-list > li').forEach(item => { item.inert = false; });
      if (this.nav && this.navSlot) { this.navSlot.append(this.nav); this.nav.removeAttribute('data-bm-docked'); this.nav.removeAttribute('data-bm-device-open'); this.nav.removeAttribute('data-bm-obstructed'); }
      this.motion?.cancel();
      if (this.section) this.section.style.paddingBottom = '';
      document.documentElement.style.removeProperty('--bm-controller-inset');
      document.dispatchEvent(new CustomEvent('plndr:controller-change', { detail: { height: 0, docked: false } }));
      super.disconnectedCallback();
    }
  });
})();
